// Day picker backed by the existing free-text `workdays` string (e.g. "Mon-Fri").
// Parses ranges and lists into selected days, and serializes the selection back
// into the same compact, human-readable string so the stored format is unchanged.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DAY_ALIASES = {
  mon: "Mon", monday: "Mon",
  tue: "Tue", tues: "Tue", tuesday: "Tue",
  wed: "Wed", weds: "Wed", wednesday: "Wed",
  thu: "Thu", thur: "Thu", thurs: "Thu", thursday: "Thu",
  fri: "Fri", friday: "Fri",
  sat: "Sat", saturday: "Sat",
  sun: "Sun", sunday: "Sun",
};

export function parseWorkdays(value) {
  const text = String(value || "").trim();

  if (!text) {
    return [];
  }

  const selected = new Set();

  text.split(",").forEach((part) => {
    const token = part.trim();

    if (!token) {
      return;
    }

    if (token.includes("-")) {
      const [startRaw, endRaw] = token.split("-").map((segment) => segment.trim().toLowerCase());
      const start = DAY_ALIASES[startRaw];
      const end = DAY_ALIASES[endRaw];

      if (!start || !end) {
        return;
      }

      const startIdx = DAYS.indexOf(start);
      const endIdx = DAYS.indexOf(end);

      if (startIdx <= endIdx) {
        for (let i = startIdx; i <= endIdx; i += 1) {
          selected.add(DAYS[i]);
        }
      } else {
        // Wrap-around range (e.g. "Sat-Mon").
        for (let i = startIdx; i < DAYS.length; i += 1) {
          selected.add(DAYS[i]);
        }
        for (let i = 0; i <= endIdx; i += 1) {
          selected.add(DAYS[i]);
        }
      }
    } else {
      const day = DAY_ALIASES[token.toLowerCase()];

      if (day) {
        selected.add(day);
      }
    }
  });

  return DAYS.filter((day) => selected.has(day));
}

export function formatWorkdays(days) {
  const set = new Set(days);
  const ordered = DAYS.filter((day) => set.has(day));

  if (!ordered.length) {
    return "";
  }

  // Collapse contiguous runs into ranges (Mon,Tue,Wed,Thu,Fri -> "Mon-Fri").
  const runs = [];
  let prevIdx = -2;

  ordered.forEach((day) => {
    const idx = DAYS.indexOf(day);

    if (idx === prevIdx + 1) {
      runs[runs.length - 1].end = day;
    } else {
      runs.push({ start: day, end: day });
    }

    prevIdx = idx;
  });

  return runs
    .map((run) => (run.start === run.end ? run.start : `${run.start}-${run.end}`))
    .join(",");
}

export default function WorkdaysPicker({ value, onChange, disabled = false }) {
  const selected = new Set(parseWorkdays(value));

  const toggle = (day) => {
    const next = new Set(selected);

    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }

    onChange(formatWorkdays(Array.from(next)));
  };

  return (
    <div className="workdays-picker" role="group" aria-label="Working days">
      {DAYS.map((day) => {
        const active = selected.has(day);

        return (
          <button
            key={day}
            type="button"
            className={`workdays-day${active ? " is-active" : ""}`}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => toggle(day)}
          >
            {day}
          </button>
        );
      })}
    </div>
  );
}
